import { useRouter } from '@tanstack/react-router';
import { lazy, Suspense, type JSX } from 'react';
import { useLocation } from '@/services/router/compat';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { getWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';
import { updateBrandWizardDraft } from '../wizard/draft-store';
import { isBrandRunBannerRoute, STYLES_ROUTE } from './banner-routes';
import { useHasResumableBrandRun } from './resumable-run';
import { requestBrandRunIntent } from './run-intent';
import { useBrandRunTracker } from './use-brand-run-tracker';

const LazyBrandRunSnackbar = lazy(() => import('./brand-run-snackbar').then((m) => ({ default: m.BrandRunSnackbar })));

type HostBodyProps = { workspaceId: number };

function HostBody({ workspaceId }: HostBodyProps): JSX.Element | null {
  const { draft, state } = useBrandRunTracker(workspaceId);
  const safeNavigate = useSafeNavigate();
  const router = useRouter();
  if (draft == null || state == null || draft.toastDismissed === true) return null;
  const goToStyles = () => {
    // "Review" should land IN the review, not merely on the page that offers
    // it. Same hand-off the page banner uses: request the action, then
    // navigate, and the wizard consumes it as it mounts on Styles.
    //
    // Only the two Review states carry an intent. Every other state labels the
    // action a plain "View", which must stay a navigation: sending this
    // state's banner intent would retry or restart a run on a click that only
    // said "take me there".
    if (state.kind === 'ready') requestBrandRunIntent('enter', workspaceId);
    else if (state.kind === 'extract_ready') requestBrandRunIntent('review', workspaceId);
    safeNavigate(router.buildLocation({ to: STYLES_ROUTE, params: { workspaceId: String(workspaceId) } }).href);
  };
  return (
    <Suspense fallback={null}>
      <LazyBrandRunSnackbar
        state={state}
        onAction={goToStyles}
        onClose={() => updateBrandWizardDraft({ toastDismissed: true }, { workspaceId, authorRun: draft.authorRun })}
      />
    </Suspense>
  );
}

/**
 * Mounted once in the authenticated layout. Renders nothing (and loads nothing
 * extra) unless this tab holds a brand generation for the current workspace,
 * and stays out of the way on the Design Studio pages, which carry the run's
 * banner above their masthead — otherwise those pages would say it twice.
 */
export function BrandRunSnackbarHost(): JSX.Element | null {
  const { pathname } = useLocation();
  const workspaceId = getWorkspaceIdFromCurrentUrl(pathname);
  // The same predicate the Styles page mounts its wizard on, so the two
  // surfaces never disagree about whether there is a run to show.
  const hasRun = useHasResumableBrandRun(workspaceId);
  if (!hasRun || workspaceId == null) return null;
  if (isBrandRunBannerRoute(pathname)) return null;
  return <HostBody workspaceId={workspaceId} />;
}
